import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import icons from '../../assets/icons/iconMap';
import SearchableSelect from '../Common/SearchableSelect';
import {
  SectionCard, SectionHeader, SectionBody, AccordionIcon, EmptyFieldsMessage,
  FieldsRow, FieldCol, FieldLabel, FieldInput, SectionDisabledNotice
} from '../../pages/PAI/RegistroPAI.styles';

/**
 * VaccineCard — Tarjeta de sección (vacuna o datos del paciente).
 * Funciona como Acordeón.
 *
 * Props:
 *  - item: { id, name, fields[], color? }
 *  - sectionId: string
 *  - isPatient: bool
 *  - defaultExpanded: bool
 *  - entryData: { [fieldKey]: value }
 *  - onChange: (fieldKey, value) => void
 *  - onBlur: (fieldKey, value) => void
 *  - historicalData: array of previous records
 */
const VaccineCard = ({ item, sectionId, isPatient = false, defaultExpanded = false, forceExpand = false, currentUserId = null, entryData = {}, onChange, onClone, onBlur, tiposIdentificacion = [], vacunadores = [], historicalData = [], removable = false, onRemove, onUpdateHistorical, showErrors = false, fieldErrors = {}, fieldSuggestions = {}, disabled = false, disabledReason = '', catalogos = null, lockedFields = {} }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editingHistoryTipo, setEditingHistoryTipo] = useState(null);
  const [historyDraft, setHistoryDraft] = useState({});
  const [savingHistory, setSavingHistory] = useState(false);
  const actionIconSize = 12;

  useEffect(() => {
    if (forceExpand) {
      setExpanded(true);
    }
  }, [forceExpand]);

  // Colores del header: vienen directamente desde la BD (item.color para paciente, item.bg_color para vacunas)
  const headerBg = isPatient ? (item.color || '#e9ecef') : (item.bg_color || '#e9ecef');
  const headerColor = isPatient ? (item.text_color || '#000') : (item.text_color || '#a35b2eff');
  const canRemove = Boolean(removable && onRemove && !isPatient && item.grupo !== 'group-3');
  const canEditHistory = Boolean(onUpdateHistorical);

  const isRequiredField = (field) => {
    if (!field) return false;
    if (typeof field.label !== 'string') return false;
    return field.label.includes('*');
  };

  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    return value === '';
  };

  const CATALOG_OPTION_KEYS = ['api_paises', 'api_departamentos', 'api_municipios', 'api_aseguradoras'];

  const normalizeText = (txt) => String(txt || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

  /**
   * Construye las opciones { value, label } para selects alimentados por catálogos de BD.
   * Si el valor actual no está en el catálogo, se agrega para que siempre sea visible.
   */
  const getCatalogOptions = (optionKey, currentValue) => {
    const cats = catalogos || {};
    let opts = [];

    if (optionKey === 'api_paises') {
      const listaPaises = (cats.paises && cats.paises.length > 0)
        ? cats.paises
        : (cats.elementos?.pais || []);

      opts = listaPaises.map((p, idx) => {
        if (typeof p === 'object' && p !== null) {
          return {
            value: Number(p.value ?? idx + 1),
            label: p.label || p.description || String(p.value)
          };
        }
        return { value: idx + 1, label: String(p) };
      });

      const seen = new Set();
      opts = opts.filter(o => {
        if (seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      });

      return opts;
    } else if (optionKey === 'api_departamentos') {
      opts = (cats.departamentos || []).map(d => ({ value: d.nombre_departamento, label: d.nombre_departamento }));
    } else if (optionKey === 'api_municipios') {
      const depto = normalizeText(entryData.depto_residencia);
      opts = (cats.municipios || [])
        .filter(m => !depto || normalizeText(m.nombre_departamento) === depto)
        .map(m => ({ value: m.nombre_municipio, label: m.nombre_municipio }));
    } else if (optionKey === 'api_aseguradoras') {
      opts = (cats.aseguradoras || []).map(a => ({
        value: a.codigo,
        label: `${a.nombre} - ${a.codigo}${a.regimen ? ` (${a.regimen})` : ''}`
      }));
    }

    const seen = new Set();
    opts = opts.filter(o => {
      const k = normalizeText(o.value);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    if (currentValue && !seen.has(normalizeText(currentValue))) {
      opts = [{ value: currentValue, label: currentValue }, ...opts];
    }

    return opts;
  };

  const resolveCatalogValue = (optionKey, currentValue) => {
    if (currentValue === null || currentValue === undefined || currentValue === '') return '';
    const opts = getCatalogOptions(optionKey, currentValue);
    const cats = catalogos || {};

    if (optionKey === 'api_paises') {
      const numVal = Number(currentValue);
      if (!Number.isNaN(numVal)) {
        const matchNum = opts.find(o => Number(o.value) === numVal);
        if (matchNum) return Number(matchNum.value);
      }
      const normRaw = normalizeText(currentValue);
      const matchText = opts.find(o => normalizeText(o.label) === normRaw);
      if (matchText) return Number(matchText.value);
      return currentValue;
    }

    if (optionKey === 'api_municipios') {
      const normCur = normalizeText(currentValue);
      const match = (cats.municipios || []).find(m =>
        normalizeText(m.nombre_municipio) === normCur ||
        String(m.id_muni_completo || '').trim() === String(currentValue).trim() ||
        String(m.codigo_municipio || '').trim() === String(currentValue).trim()
      );
      if (match) return match.nombre_municipio;
    }

    if (optionKey === 'api_aseguradoras') {
      const normCur = normalizeText(currentValue);
      // Coincidencia exacta por código oficial
      const matchCod = (cats.aseguradoras || []).find(a => normalizeText(a.codigo) === normCur);
      if (matchCod) return matchCod.codigo;

      // Coincidencia por nombre (soporte defensivo para datos antiguos)
      const userReg = entryData?.regimen_afiliacion;
      const regLetter = (userReg === 1 || userReg === '1' || userReg === 'CONTRIBUTIVO') ? 'C'
        : (userReg === 2 || userReg === '2' || userReg === 'SUBSIDIADO') ? 'S' : null;

      const matchesByName = (cats.aseguradoras || []).filter(a =>
        normalizeText(a.nombre) === normCur ||
        normalizeText(a.nombre).includes(normCur) ||
        normCur.includes(normalizeText(a.nombre))
      );

      if (matchesByName.length > 0) {
        if (regLetter) {
          const matchWithReg = matchesByName.find(a => a.regimen === regLetter);
          if (matchWithReg) return matchWithReg.codigo;
        }
        return matchesByName[0].codigo;
      }

      return currentValue;
    }

    if (optionKey === 'api_departamentos') {
      const normCur = normalizeText(currentValue);
      const match = (cats.departamentos || []).find(d =>
        normalizeText(d.nombre_departamento) === normCur ||
        String(d.codigo_departamento || '').trim() === String(currentValue).trim()
      );
      if (match) return match.nombre_departamento;
    }

    const match = opts.find(o => normalizeText(o.value) === normalizeText(currentValue));
    return match ? match.value : currentValue;
  };

  /**
   * Obtiene la lista de opciones dinámicas de dbo.elementosisa para un campo dado.
   */
  const getElementosForField = (field) => {
    if (!field) return null;
    const elementos = catalogos?.elementos;
    if (!elementos) return null;

    // 1. Coincidencia directa por key del campo
    if (Array.isArray(elementos[field.key]) && elementos[field.key].length > 0) {
      return elementos[field.key];
    }

    // 2. Mapeos de campos con alias o llaves alternativas
    if (field.key === 'pais_nacimiento' || field.key === 'pais_residencia' || field.key === 'pais') {
      return elementos.pais || elementos.paises || null;
    }
    if (field.key === 'madre_regimen') {
      return elementos.madre_regimen || elementos.regimen_afiliacion || null;
    }
    if (field.key === 'madre_etnia' || field.key === 'pertenencia_etnica') {
      return elementos.pertenencia_etnica || elementos.madre_etnia || null;
    }
    if (field.key === 'ingreso_aplicativo' || field.key === 'ingreso_aplicativo_pai') {
      return elementos.ingreso_aplicativo_pai || elementos.ingreso_aplicativo || elementos.si_no || null;
    }
    if (field.key === 'cuidador_desplazado' || field.key === 'madre_desplazado') {
      return elementos[field.key] || elementos.desplazado || elementos.si_no || null;
    }
    if (field.key === 'contraindica_vacunacion' || field.key === 'reaccion_biologicos') {
      return elementos[field.key] || elementos.si_no || null;
    }

    // 3. Si las opciones son ["SI", "NO"] o ["S", "N"], usar catálogo global si_no
    if (Array.isArray(field.options) && field.options.length === 2) {
      const uOpts = field.options.map(o => String(o).trim().toUpperCase());
      if (uOpts.includes('SI') && uOpts.includes('NO')) {
        return elementos.si_no || null;
      }
    }

    return null;
  };

  const normalizeSelectValue = (field, val) => {
    if (field.type !== 'select') return val;
    if (val === undefined || val === null || val === '') return '';

    const elementos = getElementosForField(field);
    if (elementos && elementos.length > 0) {
      const numVal = Number(val);
      if (!Number.isNaN(numVal)) {
        const matchNum = elementos.find((el) => Number(el.value) === numVal);
        if (matchNum) return Number(matchNum.value);
      }
      const normRaw = normalizeText(val);
      const matchText = elementos.find((el) =>
        normalizeText(el.label) === normRaw ||
        (el.alias && normalizeText(el.alias) === normRaw)
      );
      if (matchText) return Number(matchText.value);
      return val;
    }

    if (!field.options) return val;
    if (typeof val !== 'string') return val;
    if (field.options[0] && String(field.options[0]).startsWith('api_')) return val;
    const normalized = val.replace(/\s+/g, '_');
    return field.options.includes(normalized) ? normalized : val;
  };

  /**
   * Determina las opciones normalizadas { value, label }, el valor resuelto y si requiere buscador (searchable)
   */
  const getFieldSelectConfig = (field, rawValue) => {
    if (field.type !== 'select') {
      return null;
    }

    const firstOpt = Array.isArray(field.options) && field.options.length > 0 ? field.options[0] : null;
    const isCatalog = (firstOpt && CATALOG_OPTION_KEYS.includes(firstOpt)) ||
      (CATALOG_OPTION_KEYS.includes(`api_${field.key}`) || ['pais_nacimiento', 'pais_residencia', 'depto_residencia', 'municipio_residencia', 'aseguradora'].includes(field.key));

    if (isCatalog) {
      const catalogKey = firstOpt && CATALOG_OPTION_KEYS.includes(firstOpt)
        ? firstOpt
        : (['pais_nacimiento', 'pais_residencia'].includes(field.key) ? 'api_paises'
          : field.key === 'depto_residencia' ? 'api_departamentos'
          : field.key === 'municipio_residencia' ? 'api_municipios'
          : field.key === 'aseguradora' ? 'api_aseguradoras'
          : `api_${field.key}`);

      const opts = getCatalogOptions(catalogKey, rawValue);
      const curVal = resolveCatalogValue(catalogKey, rawValue);
      return {
        options: opts,
        searchable: true,
        value: curVal || '',
      };
    }

    const isTipoId = firstOpt === 'api_tipos_id' || ['tipo_identificacion', 'madre_tipo_id', 'cuidador_tipo_id'].includes(field.key);
    if (isTipoId) {
      let opts = tiposIdentificacion.map((tipo) => ({
        value: tipo.Codigo,
        label: `${tipo.Codigo} - ${tipo.Descripcion}`
      }));
      if (rawValue && !opts.some(o => o.value === rawValue)) {
        opts = [{ value: rawValue, label: rawValue }, ...opts];
      }
      return {
        options: opts,
        searchable: true,
        value: rawValue || '',
      };
    }

    const isVacunador = firstOpt === 'api_vacunadores' || field.key === 'responsable';
    if (isVacunador) {
      let opts = vacunadores.map((u) => ({
        value: String(u.id),
        label: u.nombre
      }));
      const strVal = rawValue !== undefined && rawValue !== null && rawValue !== '' ? String(rawValue) : '';
      if (strVal && !opts.some(o => o.value === strVal)) {
        opts = [{ value: strVal, label: strVal }, ...opts];
      }
      return {
        options: opts,
        searchable: opts.length > 5,
        value: strVal,
      };
    }

    // Catálogos dinámicos globales desde dbo.elementosisa (consultados por key del campo)
    const elementos = getElementosForField(field);
    if (elementos && elementos.length > 0) {
      const opts = elementos.map((el) => ({
        value: Number(el.value),
        label: el.label
      }));

      let curVal = '';
      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        const numVal = Number(rawValue);
        if (!Number.isNaN(numVal)) {
          const matchNum = elementos.find((el) => Number(el.value) === numVal);
          if (matchNum) curVal = Number(matchNum.value);
        }
        if (curVal === '') {
          const normRaw = normalizeText(rawValue);
          const matchText = elementos.find((el) =>
            normalizeText(el.label) === normRaw ||
            (el.alias && normalizeText(el.alias) === normRaw)
          );
          if (matchText) curVal = Number(matchText.value);
        }
        if (curVal === '') {
          curVal = rawValue;
          if (!opts.some(o => o.value === curVal)) {
            opts.unshift({ value: curVal, label: String(curVal) });
          }
        }
      }

      // Buscador únicamente si la lista contiene más de 6 opciones
      const searchable = opts.length > 6;

      return {
        options: opts,
        searchable,
        value: curVal !== undefined && curVal !== null ? curVal : '',
      };
    }

    // Opciones estáticas / fijas (fallback para campos que aún tengan options en JSON)
    if (Array.isArray(field.options) && field.options.length > 0) {
      const opts = field.options.map((opt) => ({
        value: opt,
        label: String(opt).replace(/_/g, ' ')
      }));

      let curVal = rawValue;
      if (curVal && !opts.some(o => o.value === curVal)) {
        opts.unshift({ value: curVal, label: String(curVal).replace(/_/g, ' ') });
      }

      // Activar buscador únicamente si la lista contiene más de 6 opciones
      const searchable = opts.length > 6;

      return {
        options: opts,
        searchable,
        value: curVal || '',
      };
    }

    // Si es tipo select pero no tiene opciones ni en elementosisa
    return {
      options: [],
      searchable: false,
      value: rawValue || '',
    };
  };

  const startEditHistory = (h) => {
    const recordId = h.hist_id;
    const recordTipo = h.hist_tipo;
    if (!recordId) {
      toast.error('No se pudo editar este registro (ID no encontrado)');
      return;
    }

    const creadoPor = h.creado_por;
    if (!currentUserId || !creadoPor || String(creadoPor) !== String(currentUserId)) {
      toast.error('Solo el usuario que creó el registro puede editarlo.');
      return;
    }

    const draft = {};
    item.fields.forEach(f => {
      draft[f.key] = normalizeSelectValue(f, h[f.key] ?? '');
    });
    setEditingHistoryId(recordId);
    setEditingHistoryTipo(recordTipo || null);
    setHistoryDraft(draft);
  };

  const cancelEditHistory = () => {
    setEditingHistoryId(null);
    setEditingHistoryTipo(null);
    setHistoryDraft({});
  };

  const saveEditHistory = async () => {
    if (!editingHistoryId) return;
    if (!onUpdateHistorical) return;

    try {
      const requiredMissing = (item.fields || []).some((f) => {
        if (!isRequiredField(f)) return false;
        return isEmptyValue(historyDraft[f.key]);
      });
      if (requiredMissing) {
        toast.error('Faltan campos obligatorios por diligenciar.');
        return;
      }

      setSavingHistory(true);
      await onUpdateHistorical({
        tipo: editingHistoryTipo,
        id: editingHistoryId,
        data: historyDraft
      });
      toast.success('Registro actualizado');
      cancelEditHistory();
    } catch (e) {
      toast.error(e.message || 'Error actualizando el registro');
    } finally {
      setSavingHistory(false);
    }
  };

  const handleCopyHistoryRow = (h) => {
    const payloadToClone = {};
    item.fields.forEach(f => {
      let val = h[f.key];
      payloadToClone[f.key] = '';
      if (val !== undefined && val !== null && val !== '') {
        if (f.type === 'select') {
          val = normalizeSelectValue(f, val);
        }
        payloadToClone[f.key] = val;
      }
    });

    if (onClone) {
      onClone(payloadToClone);
      return;
    }

    item.fields.forEach(f => {
      onChange?.(f.key, payloadToClone[f.key]);
    });
  };

  return (
    <SectionCard id={sectionId}>
      <SectionHeader
        $bg={headerBg}
        $color={headerColor}
        $expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{item.name}</span>
          {disabled && (
            <span
              style={{
                fontSize: '11px',
                backgroundColor: 'rgba(0,0,0,0.12)',
                padding: '1px 6px',
                borderRadius: '4px',
                fontWeight: 600
              }}
            >
              No aplica
            </span>
          )}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {canRemove && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
              style={{
                background: 'transparent',
                border: '1px solid rgba(0,0,0,0.25)',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '0 6px',
                lineHeight: '18px',
                fontSize: '14px',
                color: headerColor
              }}
              title="Quitar del formulario"
            >
              ✕
            </button>
          )}
          <AccordionIcon>{expanded ? '▾' : '▸'}</AccordionIcon>
        </span>
      </SectionHeader>

      {expanded && (
        <SectionBody>
          {disabled && disabledReason && (
            <SectionDisabledNotice>{disabledReason}</SectionDisabledNotice>
          )}
          {item.fields && item.fields.length > 0 ? (
            <FieldsRow>
              {item.fields.map((field, idx) => {
                const value = entryData[field.key] || '';
                const inputId = `${sectionId}_${field.key}_${idx}`;
                const hasError = Boolean(!disabled && showErrors && fieldErrors && fieldErrors[field.key]);
                const suggestions = Array.isArray(fieldSuggestions[field.key]) ? fieldSuggestions[field.key] : [];
                const listId = suggestions.length > 0 ? `${inputId}_datalist` : undefined;
                const selectConfig = getFieldSelectConfig(field, value);

                return (
                  <FieldCol key={idx}>
                    <FieldLabel htmlFor={inputId} title={field.label}>
                      {field.label}
                    </FieldLabel>
                    {selectConfig ? (
                      <SearchableSelect
                        id={inputId}
                        name={field.key}
                        value={selectConfig.value}
                        options={selectConfig.options}
                        searchable={selectConfig.searchable}
                        multiple={false}
                        disabled={disabled}
                        error={hasError}
                        placeholder="- Seleccione -"
                        onValueChange={(val) => onChange && onChange(field.key, val)}
                        size="small"
                        sx={{ minWidth: '100%', width: '100%' }}
                        selectSx={{
                          height: 'calc(var(--app-font-size, 13px) * 2 + 2px)',
                          minHeight: '28px',
                          fontSize: 'var(--app-font-size, 13px)',
                          backgroundColor: disabled ? '#e9ecef !important' : 'white',
                          '&.Mui-disabled': {
                            backgroundColor: '#e9ecef !important',
                            color: '#6c757d',
                            cursor: 'not-allowed',
                            opacity: 0.75,
                          },
                          '& .MuiSelect-select': {
                            py: '2px !important',
                            pr: '26px !important',
                            fontSize: 'var(--app-font-size, 13px)',
                            lineHeight: '22px',
                          },
                          '& .MuiSelect-icon': {
                            fontSize: '18px',
                            right: '3px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: hasError ? '#dc3545 !important' : '#ced4da',
                          },
                          ...(hasError ? {
                            boxShadow: '0 0 0 1px #dc3545',
                          } : {})
                        }}
                      />
                    ) : (
                      <>
                        <FieldInput
                          id={inputId}
                          type={field.type === 'input' ? 'text' : field.type}
                          value={value}
                          list={listId}
                          disabled={disabled}
                          onChange={e => onChange && onChange(field.key, e.target.value)}
                          onBlur={e => onBlur && onBlur(field.key, e.target.value)}
                          style={hasError ? { borderColor: '#dc3545', boxShadow: '0 0 0 1px #dc3545' } : undefined}
                        />
                        {listId && (
                          <datalist id={listId}>
                            {suggestions.map((suggestion) => (
                              <option key={suggestion} value={suggestion} />
                            ))}
                          </datalist>
                        )}
                      </>
                    )}
                  </FieldCol>
                );
              })}
            </FieldsRow>
          ) : (
            <EmptyFieldsMessage>Sin campos</EmptyFieldsMessage>
          )}

          {historicalData && historicalData.length > 0 && (
            <div style={{ marginTop: '20px', borderTop: '1px dashed #ced4da', paddingTop: '10px' }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setHistoryExpanded(v => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setHistoryExpanded(v => !v);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '13px',
                  color: '#6c757d',
                  marginBottom: historyExpanded ? '10px' : '0',
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <span>Historial de Registros</span>
                <span style={{ fontSize: '16px', lineHeight: 1 }}>{historyExpanded ? '▾' : '▸'}</span>
              </div>

              {historyExpanded && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                        <th style={{ padding: '8px 4px', color: '#495057', minWidth: '100px' }}>fechaRegistro</th>
                        {item.fields.map(f => <th key={f.key} style={{ padding: '8px 4px', color: '#495057' }}>{f.label.replace('*', '')}</th>)}
                        <th style={{ padding: '8px 4px', color: '#495057', width: '50px' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historicalData.map((h, i) => {
                        const recordId = h.hist_id;
                        const recordTipo = h.hist_tipo;
                        const isEditing = canEditHistory && recordId && String(editingHistoryId) === String(recordId) && String(editingHistoryTipo) === String(recordTipo);
                        const canCopy = Boolean((onClone || onChange) && !disabled);
                        const canEditRow = canEditHistory && currentUserId && h.creado_por && String(h.creado_por) === String(currentUserId);

                        return (
                          <tr key={recordId || i} style={{ borderBottom: '1px solid #e9ecef' }}>
                            <td style={{ padding: '8px 4px', color: '#212529' }}>{h.fechaRegistro || '-'}</td>
                            {item.fields.map(f => {
                              if (!isEditing) {
                                let val = h[f.key];

                                if (f.type === 'select') {
                                  const isVacunador = (f.options && f.options[0] === 'api_vacunadores') || f.key === 'responsable';
                                  const isTipoId = (f.options && f.options[0] === 'api_tipos_id') || ['tipo_identificacion', 'madre_tipo_id', 'cuidador_tipo_id'].includes(f.key);

                                  if (isVacunador && vacunadores.length > 0) {
                                    const found = vacunadores.find(v => String(v.id) === String(val));
                                    if (found) val = found.nombre;
                                  } else if (isTipoId && tiposIdentificacion.length > 0) {
                                    const found = tiposIdentificacion.find(t => String(t.Codigo) === String(val));
                                    if (found) val = `${found.Codigo} - ${found.Descripcion}`;
                                  } else {
                                    const elementos = getElementosForField(f);
                                    if (elementos && elementos.length > 0) {
                                      const numVal = Number(val);
                                      const match = elementos.find(el =>
                                        (!Number.isNaN(numVal) && Number(el.value) === numVal) ||
                                        normalizeText(el.label) === normalizeText(val) ||
                                        (el.alias && normalizeText(el.alias) === normalizeText(val))
                                      );
                                      if (match) val = match.label;
                                    } else if (typeof val === 'string') {
                                      val = val.replace(/_/g, ' ');
                                    }
                                  }
                                }

                                return <td key={f.key} style={{ padding: '8px 4px', color: '#212529' }}>{val || '-'}</td>;
                              }

                              const value = historyDraft[f.key] ?? '';
                              const isLocked = ['fechaRegistro', 'fecha_aplicacion', 'fecha_atencion', 'fecha_registro', 'fecha_antecedente'].includes(f.key);

                              if (isLocked) {
                                return <td key={f.key} style={{ padding: '8px 4px', color: '#212529' }}>{h[f.key] || '-'}</td>
                              }

                              if (f.type === 'select') {
                                const hSelectConfig = getFieldSelectConfig(f, value);
                                return (
                                  <td key={f.key} style={{ padding: '4px 2px', minWidth: '130px' }}>
                                    <SearchableSelect
                                      name={f.key}
                                      value={hSelectConfig ? hSelectConfig.value : value}
                                      options={hSelectConfig ? hSelectConfig.options : []}
                                      searchable={hSelectConfig ? hSelectConfig.searchable : false}
                                      multiple={false}
                                      placeholder="-"
                                      onValueChange={val => setHistoryDraft(prev => ({ ...prev, [f.key]: val }))}
                                      size="small"
                                      sx={{ minWidth: '100%', width: '100%' }}
                                      selectSx={{
                                        height: '26px',
                                        minHeight: '26px',
                                        fontSize: '12px',
                                        backgroundColor: 'white',
                                        '& .MuiSelect-select': {
                                          py: '1px !important',
                                          pr: '22px !important',
                                          fontSize: '12px',
                                          lineHeight: '22px',
                                        },
                                        '& .MuiSelect-icon': {
                                          fontSize: '16px',
                                          right: '2px',
                                        }
                                      }}
                                    />
                                  </td>
                                );
                              }

                              return (
                                <td key={f.key} style={{ padding: '6px 4px' }}>
                                  <FieldInput
                                    type={f.type === 'input' ? 'text' : f.type}
                                    value={value}
                                    onChange={e => setHistoryDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    style={{ height: '26px', fontSize: '12px' }}
                                  />
                                </td>
                              );
                            })}
                            <td style={{ padding: '8px 4px' }}>
                              {canEditHistory && isEditing ? (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    type="button"
                                    disabled={savingHistory}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      saveEditHistory();
                                    }}
                                    style={{ background: 'transparent', border: '1px solid #cbd3da', borderRadius: '4px', cursor: 'pointer', padding: '4px 6px', fontSize: '14px' }}
                                    title="Guardar"
                                  >
                                    <img
                                      alt="Buscar paciente"
                                      title="Guardar"
                                      src={icons.enter}
                                      className="hover"
                                      style={{ width: `${actionIconSize}px`, height: `${actionIconSize}px`, cursor: 'pointer' }}
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={savingHistory}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      cancelEditHistory();
                                    }}
                                    style={{ background: 'transparent', border: '1px solid #cbd3da', borderRadius: '4px', cursor: 'pointer', padding: '4px 6px', fontSize: '14px' }}
                                    title="Cancelar"
                                  >
                                    <span className="hover" style={{ display: 'inline-block', width: `${actionIconSize}px`, height: `${actionIconSize}px`, lineHeight: `${actionIconSize}px`, textAlign: 'center', fontSize: `${actionIconSize}px` }}>✕</span>
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    type="button"
                                    disabled={!canEditRow}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      startEditHistory(h);
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: '1px solid #cbd3da',
                                      borderRadius: '4px',
                                      cursor: canEditRow ? 'pointer' : 'not-allowed',
                                      padding: '4px 6px',
                                      fontSize: '14px',
                                      opacity: canEditRow ? 1 : 0.45
                                    }}
                                    title="Editar"
                                  >
                                    <img
                                      alt="Editar teléfono"
                                      className={canEditRow ? "hover" : ""}
                                      src={icons.pencil}
                                      style={{ width: `${actionIconSize}px`, height: `${actionIconSize}px`, cursor: 'pointer', verticalAlign: 'middle' }}
                                    />
                                  </button>
                                  {canCopy && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleCopyHistoryRow(h);
                                      }}
                                      style={{ background: 'transparent', border: '1px solid #cbd3da', borderRadius: '4px', cursor: 'pointer', padding: '4px 6px', fontSize: '14px' }}
                                      title="Copiar"
                                    >
                                      <img
                                        alt="Gráficas"
                                        title="Copiar"
                                        src={icons.book}
                                        className="hover"
                                        style={{ width: `${actionIconSize}px`, height: `${actionIconSize}px`, cursor: 'pointer' }}
                                      />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </SectionBody>
      )}
    </SectionCard>
  );
};

export default VaccineCard;
